import React from 'react';

import { View } from '@rbilabs/universal-components';
import { VideoView, useVideoPlayer } from 'expo-video';
import { useIntl } from 'react-intl';
import { spacing } from 'ucl/themes/shared/primitives';

import { IServerOrder } from '@rbi-ctg/menu';
import { takeoverAudioMuted } from 'components/app-takeover/play-audio-once';
import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import useOrderNumber from 'hooks/use-order-number';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isWeb, region } from 'utils/environment';
import { Region } from 'utils/environment/types';

import { Container, Header, Instructions, OrderNumber } from './styled';
import { getOrderConfirmationHeader, getOrderConfirmationInstructions } from './utils';

interface IOrderConfirmationHeader {
  serverOrder: IServerOrder;
}
const OrderConfirmationHeader = ({ serverOrder }: IOrderConfirmationHeader) => {
  const { user } = useAuthContext();
  const { formatMessage } = useIntl();
  const orderNumber = useOrderNumber(serverOrder);
  const isMobileOrderForDriveThruLaneEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE
  );
  const formatedOrderNumber = formatMessage({ id: 'orderConfirmationNumber' }, { orderNumber });
  const serviceMode = serverOrder.cart.serviceMode;
  const userName = (user?.details.name ?? '').split(' ')[0];

  const {
    headerVideo: takeoverVideo,
    headerFontColor: takeoverFontColor,
    headerBackgroundColor: takeoverBackgroundColor,
    headerVideoAspectRatio: takeoverAspectRatio,
    ConfirmHeaderOverlay: TakeoverConfirmHeaderOverlay,
  } = useAppTakeover()?.orderConfirmDetails || {};

  const headerText = getOrderConfirmationHeader({ userName, formatMessage });
  const instructions = getOrderConfirmationInstructions({
    userName,
    serviceMode,
    formatMessage,
    isMobileOrderForDriveThruLaneEnabled,
  });

  return (
    <Container
      style={{
        marginHorizontal: spacing.$4 * -1,
        marginTop: spacing.$4 * -1,
      }}
    >
      <View
        style={{
          backgroundColor: takeoverBackgroundColor,
          paddingTop: spacing.$4,
          paddingHorizontal: spacing.$4,
          marginBottom: -1, // fixes mysterious 1px gap between video and view on android
        }}
      >
        {orderNumber && (
          <OrderNumber focusable color={takeoverFontColor}>
            {formatedOrderNumber}
          </OrderNumber>
        )}
        {!!headerText && !!userName && (
          <Header focusable color={takeoverFontColor}>
            {headerText}
          </Header>
        )}
        <Instructions focusable color={takeoverFontColor}>
          {instructions}
        </Instructions>
      </View>
      {TakeoverConfirmHeaderOverlay && (
        <TakeoverConfirmHeaderOverlay backgroundColor={takeoverBackgroundColor} />
      )}
      {takeoverVideo && <TakeoverVideo video={takeoverVideo} aspectRatio={takeoverAspectRatio} />}
    </Container>
  );
};

// separate component to avoid allocating resources for this non-standard path
const TakeoverVideo = ({ video, aspectRatio }: { video: any; aspectRatio: number }) => {
  const player = useVideoPlayer(video, player => {
    player.loop = false;
    player.audioMixingMode = 'mixWithOthers';

    if (region === Region.CA || takeoverAudioMuted.current) {
      player.muted = true;
    }

    if (isWeb) {
      // web requires a timeout due to a bug, maybe could remove in SDK 54 https://github.com/expo/expo/issues/36350
      setTimeout(() => player.play(), 0);
    } else {
      player.play();
    }
  });

  return (
    <VideoView
      style={{ width: '100%', aspectRatio }}
      contentFit="cover"
      nativeControls={false}
      allowsFullscreen={false}
      allowsPictureInPicture={false}
      player={player}
      playsInline
    />
  );
};

export default OrderConfirmationHeader;
